import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { useToast } from '../hooks/use-toast';
import { ArrowLeft, MapPin, GraduationCap, Calendar, User, Star, MessageSquare, Clock, Home, Briefcase, Heart, BookOpen, Target, Moon, School, X, Video, CheckCircle, Send, ThumbsUp, Mail } from 'lucide-react';

// Helper function to get first name
const getFirstName = (fullName) => {
  if (!fullName) return '';
  return fullName.split(' ')[0];
};

// Helper function to format availability in a cleaner way
const formatAvailability = (availability) => {
  if (!availability) return null;
  
  // Check if guide has custom availability_display field
  // Otherwise try to parse and simplify the raw availability string
  return availability;
};

// Helper function to format date
const formatReviewDate = (dateString) => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const GuideProfile = () => {
  const { guideId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const [guide, setGuide] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedTourType, setSelectedTourType] = useState(null);
  // Review state
  const [reviews, setReviews] = useState([]);
  const [reviewStats, setReviewStats] = useState({ total: 0, average: 0 });
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [submittingReview, setSubmittingReview] = useState(false);
  const [reviewForm, setReviewForm] = useState({
    reviewer_name: '',
    reviewer_email: '',
    rating: 5,
    title: '',
    comment: '',
    tour_type: '',
    tour_date: ''
  });
  const [bookingForm, setBookingForm] = useState({
    name: '',
    email: '',
    phone: '',
    date: '',
    time: '',
    tourType: 'Campus tour',
    message: '',
    isCustomTimeRequest: false,
    customTimeNote: ''
  });
  const [showTourDetails, setShowTourDetails] = useState(false);

  const API = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchGuide();
  }, [guideId]);

  const fetchGuide = async () => {
    try {
      const response = await fetch(`${API}/api/guides/approved`);
      if (response.ok) {
        const guides = await response.json();
        const foundGuide = guides.find(g => g.id === guideId);
        setGuide(foundGuide);
      }
    } catch (error) {
      console.error('Failed to fetch guide:', error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch reviews for this guide
  useEffect(() => {
    if (guideId) {
      fetchReviews();
    }
  }, [guideId]);

  // Scroll to reviews section if hash is #reviews
  useEffect(() => {
    if (location.hash === '#reviews') {
      setTimeout(() => {
        const el = document.getElementById('reviews');
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 500);
    }
  }, [location.hash, guide]);

  const fetchReviews = async () => {
    try {
      const response = await fetch(`${API}/api/reviews/${guideId}`);
      if (response.ok) {
        const data = await response.json();
        setReviews(data.reviews || []);
        setReviewStats({
          total: data.total_reviews || 0,
          average: data.average_rating || 0
        });
      }
    } catch (error) {
      console.error('Failed to fetch reviews:', error);
    }
  };

  const handleReviewSubmit = async (e) => {
    e.preventDefault();
    setSubmittingReview(true);
    
    try {
      const response = await fetch(`${API}/api/reviews`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          guide_id: guideId,
          ...reviewForm
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to submit review');
      }

      toast({
        title: "Review Submitted!",
        description: "Thank you for your feedback."
      });

      // Reset form and refresh reviews
      setReviewForm({
        reviewer_name: '',
        reviewer_email: '',
        rating: 5,
        title: '',
        comment: '',
        tour_type: '',
        tour_date: ''
      });
      setShowReviewForm(false);
      fetchReviews();
    } catch (error) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive"
      });
    } finally {
      setSubmittingReview(false);
    }
  };

  const handleBookingSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      // Build the preferred date string based on booking type
      let preferredDateStr;
      if (bookingForm.isCustomTimeRequest) {
        preferredDateStr = `[TIME REQUEST] ${bookingForm.customTimeNote}`;
      } else {
        preferredDateStr = `${bookingForm.date} at ${bookingForm.time}`;
      }
      
      const response = await fetch(`${API}/api/guide-bookings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          guide_id: guide.id,
          guide_name: guide.name,
          school_name: guide.school,
          visitor_name: bookingForm.name,
          visitor_email: bookingForm.email,
          visitor_phone: bookingForm.phone,
          preferred_date: preferredDateStr,
          tour_type: bookingForm.tourType,
          message: `Tour Type: ${bookingForm.tourType}${bookingForm.isCustomTimeRequest ? '\n[Custom Time Request]' : ''}\n\n${bookingForm.message}`,
          is_time_request: bookingForm.isCustomTimeRequest
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        
        // If the tour has a price, redirect to Stripe for deposit payment
        if (data.price && data.price > 0) {
          const payRes = await fetch(`${API}/api/payments/create-deposit`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              booking_id: data.id,
              origin_url: window.location.origin
            })
          });
          
          if (payRes.ok) {
            const payData = await payRes.json();
            // Redirect to Stripe Checkout
            window.location.href = payData.url;
            return;
          }
        }
        
        // No price or payment failed — show success without payment
        toast({
          title: bookingForm.isCustomTimeRequest ? "Time Request Sent!" : "Booking Request Sent!",
          description: `Your ${bookingForm.isCustomTimeRequest ? 'time request' : 'tour request'} with ${guide.name} has been submitted.`,
        });
        setShowBookingModal(false);
        setBookingForm({ name: '', email: '', phone: '', date: '', time: '', tourType: 'Campus tour', message: '', isCustomTimeRequest: false, customTimeNote: '' });
      } else {
        throw new Error('Failed to submit booking');
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to submit booking request. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-black"></div>
        </div>
        <Footer />
      </div>
    );
  }

  if (!guide) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <div className="max-w-7xl mx-auto px-4 py-16 text-center">
          <h1 className="text-2xl font-bold mb-4">Guide Not Found</h1>
          <Button onClick={() => navigate('/explore-schools')}>
            Back to Explore Schools
          </Button>
        </div>
        <Footer />
      </div>
    );
  }

  const getFirstName = (fullName) => fullName?.split(' ')[0] || fullName;

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Back Button */}
        <Button 
          variant="ghost" 
          onClick={() => navigate(-1)}
          className="mb-6 text-gray-600 hover:text-black"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content - Left Side */}
          <div className="lg:col-span-2 space-y-6">
            
            {/* Hero Section */}
            <Card className="overflow-hidden">
              <CardContent className="p-0">
                {/* School Banner */}
                <div className="bg-gradient-to-r from-gray-900 to-gray-700 text-white px-6 py-3">
                  <a href={`/school/${guide.school}`} className="text-sm hover:underline flex items-center">
                    <GraduationCap className="w-4 h-4 mr-2" />
                    {guide.school}
                  </a>
                </div>
                
                {/* Profile Header */}
                <div className="p-6">
                  <div className="flex flex-col sm:flex-row gap-6">
                    {/* Profile Photo */}
                    <div className="flex-shrink-0">
                      {guide.photo_urls && guide.photo_urls[0] ? (
                        <img 
                          src={guide.photo_urls[0]} 
                          alt={guide.name}
                          className={`w-32 h-32 rounded-full border-4 border-white shadow-lg ${
                            guide.photo_position === 'full' ? 'object-contain' :
                            guide.photo_position === 'top' ? 'object-cover object-top' : 
                            guide.photo_position === 'bottom' ? 'object-cover object-bottom' : 
                            'object-cover object-center'
                          }`}
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.style.display = 'none';
                            e.target.nextSibling && (e.target.nextSibling.style.display = 'flex');
                          }}
                        />
                      ) : null}
                      <div className={`w-32 h-32 rounded-full bg-gray-200 items-center justify-center border-4 border-white shadow-lg ${guide.photo_urls && guide.photo_urls[0] ? 'hidden' : 'flex'}`}>
                        <User className="w-16 h-16 text-gray-400" />
                      </div>
                    </div>
                    
                    {/* Name and Bio */}
                    <div className="flex-grow">
                      <div className="flex items-center gap-3 mb-2">
                        <h1 className="text-2xl sm:text-3xl font-bold">{guide.name}</h1>
                        <span className="px-3 py-1 bg-green-100 text-green-700 text-xs font-medium rounded-full">
                          New
                        </span>
                      </div>
                      <p className="text-gray-600 leading-relaxed">{guide.bio}</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* About Me Section */}
            <Card>
              <CardContent className="p-6">
                <h2 className="text-xl font-bold mb-4 flex items-center">
                  <User className="w-5 h-5 mr-2" />
                  About me
                </h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                  {guide.gender && (
                    <div className="border-b pb-2">
                      <p className="text-xs text-gray-500 uppercase">Gender</p>
                      <p className="font-medium">{guide.gender}</p>
                    </div>
                  )}
                  {guide.academic_year && (
                    <div className="border-b pb-2">
                      <p className="text-xs text-gray-500 uppercase">Academic Year</p>
                      <p className="font-medium">{guide.academic_year}</p>
                    </div>
                  )}
                  {guide.age && (
                    <div className="border-b pb-2">
                      <p className="text-xs text-gray-500 uppercase">Age</p>
                      <p className="font-medium">{guide.age}</p>
                    </div>
                  )}
                  {guide.admission_type && (
                    <div className="border-b pb-2">
                      <p className="text-xs text-gray-500 uppercase">Admission Type</p>
                      <p className="font-medium">{guide.admission_type}</p>
                    </div>
                  )}
                  {guide.personality_type && (
                    <div className="border-b pb-2">
                      <p className="text-xs text-gray-500 uppercase">Personality</p>
                      <p className="font-medium">{guide.personality_type}</p>
                    </div>
                  )}
                  {guide.college_rating && (
                    <div className="border-b pb-2">
                      <p className="text-xs text-gray-500 uppercase">College Rating</p>
                      <p className="font-medium">{guide.college_rating}</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Tour Type Section */}
            <Card>
              <CardContent className="p-6">
                <h2 className="text-xl font-bold mb-4 flex items-center">
                  <Calendar className="w-5 h-5 mr-2" />
                  Tour type
                </h2>
                <div className="space-y-4">
                  {/* Tour Type Options */}
                  <div className="flex flex-wrap gap-3">
                    <button
                      onClick={() => setSelectedTourType(selectedTourType === 'campus' ? null : 'campus')}
                      className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                        selectedTourType === 'campus' 
                          ? 'bg-black text-white ring-2 ring-black ring-offset-2' 
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      Campus tour
                    </button>
                    <button
                      onClick={() => setSelectedTourType(selectedTourType === 'video' ? null : 'video')}
                      className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                        selectedTourType === 'video' 
                          ? 'bg-black text-white ring-2 ring-black ring-offset-2' 
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      Video Tour
                    </button>
                  </div>

                  {/* Campus Tour Details */}
                  {selectedTourType === 'campus' && (
                    <div className="bg-gray-50 rounded-xl p-5 border border-gray-200 animate-in fade-in duration-200">
                      <div className="flex items-start gap-4">
                        <div className="w-12 h-12 bg-black rounded-full flex items-center justify-center flex-shrink-0">
                          <MapPin className="w-6 h-6 text-white" />
                        </div>
                        <div className="flex-grow">
                          <h3 className="font-bold text-lg mb-2">In-Person Campus Tour</h3>
                          <p className="text-gray-600 mb-3">
                            Walk around {guide.school} with {getFirstName(guide.name)}! Get an authentic student perspective on campus life, explore key locations, and ask all your questions in person.
                          </p>
                          <ul className="space-y-2 text-sm text-gray-600">
                            <li className="flex items-center gap-2">
                              <CheckCircle className="w-4 h-4 text-green-600" />
                              <span>Duration: ~60-90 minutes</span>
                            </li>
                            <li className="flex items-center gap-2">
                              <CheckCircle className="w-4 h-4 text-green-600" />
                              <span>Visit classrooms, dorms, dining halls & more</span>
                            </li>
                            <li className="flex items-center gap-2">
                              <CheckCircle className="w-4 h-4 text-green-600" />
                              <span>Real student insights & hidden gems</span>
                            </li>
                            {guide.meeting_point && (
                              <li className="flex items-center gap-2">
                                <MapPin className="w-4 h-4 text-blue-600" />
                                <span>Meet at: {guide.meeting_point}</span>
                              </li>
                            )}
                          </ul>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Video Tour Details */}
                  {selectedTourType === 'video' && (
                    <div className="bg-blue-50 rounded-xl p-5 border border-blue-200 animate-in fade-in duration-200">
                      <div className="flex items-start gap-4">
                        <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center flex-shrink-0">
                          <Video className="w-6 h-6 text-white" />
                        </div>
                        <div className="flex-grow">
                          <h3 className="font-bold text-lg mb-2">Video Tour with {getFirstName(guide.name)}</h3>
                          <p className="text-gray-700 mb-3">
                            Chat with {getFirstName(guide.name)} for one hour to know more about {guide.school}! Perfect if you can't visit in person or want to learn more before your campus visit.
                          </p>
                          <ul className="space-y-2 text-sm text-gray-700">
                            <li className="flex items-center gap-2">
                              <CheckCircle className="w-4 h-4 text-green-600" />
                              <span>Duration: ~60 minutes</span>
                            </li>
                            <li className="flex items-center gap-2">
                              <CheckCircle className="w-4 h-4 text-green-600" />
                              <span>Ask anything about academics, social life, dorms</span>
                            </li>
                            <li className="flex items-center gap-2">
                              <CheckCircle className="w-4 h-4 text-green-600" />
                              <span>Virtual campus walkthrough available</span>
                            </li>
                            <li className="flex items-center gap-2">
                              <CheckCircle className="w-4 h-4 text-green-600" />
                              <span>Flexible scheduling from anywhere</span>
                            </li>
                          </ul>
                          <div className="mt-4 pt-4 border-t border-blue-200">
                            <p className="text-sm text-blue-800 font-medium">
                              💡 Great for: Out-of-state students, international applicants, or anyone who wants an in-depth conversation about college life at {guide.school}.
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Hint text when nothing selected */}
                  {!selectedTourType && (
                    <p className="text-sm text-gray-500 italic">
                      👆 Click on a tour type to learn more
                    </p>
                  )}
                </div>
                {guide.tour_description && (
                  <p className="mt-4 text-gray-600">{guide.tour_description}</p>
                )}
              </CardContent>
            </Card>

            {/* Academic Info */}
            <Card>
              <CardContent className="p-6">
                <h2 className="text-xl font-bold mb-4 flex items-center">
                  <BookOpen className="w-5 h-5 mr-2" />
                  Academic Info
                </h2>
                <div className="space-y-4">
                  {guide.hometown && (
                    <div className="flex items-start gap-3">
                      <MapPin className="w-5 h-5 text-gray-400 mt-0.5" />
                      <div>
                        <p className="text-xs text-gray-500 uppercase">Hometown</p>
                        <p className="font-medium">{guide.hometown}</p>
                      </div>
                    </div>
                  )}
                  {guide.major && (
                    <div className="flex items-start gap-3">
                      <GraduationCap className="w-5 h-5 text-gray-400 mt-0.5" />
                      <div>
                        <p className="text-xs text-gray-500 uppercase">Major(s)</p>
                        <p className="font-medium">{guide.major}</p>
                      </div>
                    </div>
                  )}
                  {guide.minor && (
                    <div className="flex items-start gap-3">
                      <BookOpen className="w-5 h-5 text-gray-400 mt-0.5" />
                      <div>
                        <p className="text-xs text-gray-500 uppercase">Minor(s)</p>
                        <p className="font-medium">{guide.minor}</p>
                      </div>
                    </div>
                  )}
                  {guide.high_school && (
                    <div className="flex items-start gap-3">
                      <School className="w-5 h-5 text-gray-400 mt-0.5" />
                      <div>
                        <p className="text-xs text-gray-500 uppercase">High School</p>
                        <p className="font-medium">{guide.high_school}</p>
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Extracurriculars */}
            {guide.extracurriculars && guide.extracurriculars.length > 0 && (
              <Card>
                <CardContent className="p-6">
                  <h2 className="text-xl font-bold mb-4">Extracurricular Activities</h2>
                  <div className="flex flex-wrap gap-2">
                    {guide.extracurriculars.map((activity, idx) => (
                      <span key={idx} className="px-3 py-1.5 bg-gray-100 text-gray-700 rounded-full text-sm">
                        {activity}
                      </span>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Clubs & Organizations */}
            {guide.clubs && (
              <Card>
                <CardContent className="p-6">
                  <h2 className="text-xl font-bold mb-4">Clubs, Organizations & Involvement</h2>
                  <p className="text-gray-600">{guide.clubs}</p>
                </CardContent>
              </Card>
            )}

            {/* Housing Experience */}
            {guide.housing_experience && guide.housing_experience.length > 0 && (
              <Card>
                <CardContent className="p-6">
                  <h2 className="text-xl font-bold mb-4 flex items-center">
                    <Home className="w-5 h-5 mr-2" />
                    Housing Experience
                  </h2>
                  <div className="flex flex-wrap gap-2">
                    {guide.housing_experience.map((housing, idx) => (
                      <span key={idx} className="px-3 py-1.5 bg-gray-100 text-gray-700 rounded-full text-sm">
                        {housing}
                      </span>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Personal Questions */}
            <Card>
              <CardContent className="p-6 space-y-6">
                {guide.college_experience && (
                  <div>
                    <h3 className="font-bold text-gray-900 mb-2 flex items-center">
                      <Star className="w-4 h-4 mr-2" />
                      Describe your college experience
                    </h3>
                    <p className="text-gray-600">{guide.college_experience}</p>
                  </div>
                )}
                
                {guide.tip_for_students && (
                  <div>
                    <h3 className="font-bold text-gray-900 mb-2 flex items-center">
                      <MessageSquare className="w-4 h-4 mr-2" />
                      Tip for future students
                    </h3>
                    <p className="text-gray-600">{guide.tip_for_students}</p>
                  </div>
                )}
                
                {guide.favorite_class && (
                  <div>
                    <h3 className="font-bold text-gray-900 mb-2 flex items-center">
                      <BookOpen className="w-4 h-4 mr-2" />
                      Favorite class
                    </h3>
                    <p className="text-gray-600">{guide.favorite_class}</p>
                  </div>
                )}
                
                {guide.career_goals && (
                  <div>
                    <h3 className="font-bold text-gray-900 mb-2 flex items-center">
                      <Target className="w-4 h-4 mr-2" />
                      Career goals
                    </h3>
                    <p className="text-gray-600">{guide.career_goals}</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Meeting Point */}
            {guide.meeting_point && (
              <Card>
                <CardContent className="p-6">
                  <h2 className="text-xl font-bold mb-4 flex items-center">
                    <MapPin className="w-5 h-5 mr-2" />
                    Meeting Point
                  </h2>
                  <p className="text-gray-600">{guide.meeting_point}</p>
                </CardContent>
              </Card>
            )}

            {/* Photo Gallery */}
            {guide.photo_urls && guide.photo_urls.length > 1 && (
              <Card>
                <CardContent className="p-6">
                  <h2 className="text-xl font-bold mb-4">Photos</h2>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                    {guide.photo_urls.map((url, idx) => (
                      <img 
                        key={idx}
                        src={url} 
                        alt={`${guide.name} photo ${idx + 1}`}
                        className={`w-full h-48 rounded-lg ${
                          guide.photo_position === 'full' ? 'object-contain' :
                          guide.photo_position === 'top' ? 'object-cover object-top' : 
                          guide.photo_position === 'bottom' ? 'object-cover object-bottom' : 
                          'object-cover object-center'
                        }`}
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><rect fill="%23e5e7eb" width="100" height="100"/><text x="50" y="55" text-anchor="middle" fill="%239ca3af" font-size="12">Image unavailable</text></svg>';
                        }}
                      />
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Reviews Section */}
            <Card id="reviews">
              <CardContent className="p-6">
                {/* Reviews Header */}
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-xl font-bold flex items-center">
                    <MessageSquare className="w-5 h-5 mr-2" />
                    Reviews ({reviewStats.total})
                  </h2>
                  {reviewStats.total > 0 && (
                    <div className="flex items-center gap-2">
                      <div className="flex">
                        {[1,2,3,4,5].map(star => (
                          <Star key={star} className={`w-5 h-5 ${star <= Math.round(reviewStats.average) ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'}`} />
                        ))}
                      </div>
                      <span className="text-lg font-bold">{reviewStats.average.toFixed(1)}</span>
                      <span className="text-sm text-gray-500">({reviewStats.total} {reviewStats.total === 1 ? 'review' : 'reviews'})</span>
                    </div>
                  )}
                </div>

                {/* Reviews List */}
                {reviews.length > 0 ? (
                  <div className="space-y-6 mb-6">
                    {reviews.map((review) => (
                      <div key={review.id} className="border-b pb-6 last:border-0">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <span className="font-semibold">{review.reviewer_name}</span>
                            <div className="flex">
                              {[1,2,3,4,5].map(star => (
                                <Star key={star} className={`w-4 h-4 ${star <= review.rating ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'}`} />
                              ))}
                            </div>
                            {review.is_verified && (
                              <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full flex items-center gap-1">
                                <CheckCircle className="w-3 h-3" /> Verified
                              </span>
                            )}
                          </div>
                          {review.tour_date && (
                            <span className="text-sm text-gray-500">
                              Tour: {formatReviewDate(review.tour_date)}
                            </span>
                          )}
                        </div>
                        {review.title && <p className="font-medium mt-1">{review.title}</p>}
                        <p className="text-gray-700 mt-2">{review.comment}</p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 mb-6">
                    <MessageSquare className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-500">No reviews yet.</p>
                    <p className="text-sm text-gray-400">Be the first to share your experience!</p>
                  </div>
                )}

                {/* Write Review Button - Centered at Bottom */}
                <div className="text-center">
                  <Button
                    onClick={() => setShowReviewForm(!showReviewForm)}
                    variant={showReviewForm ? "outline" : "default"}
                    className={showReviewForm ? "" : "bg-black hover:bg-gray-800"}
                  >
                    {showReviewForm ? "Cancel" : "Write a Review"}
                  </Button>
                </div>

                {/* Write Review Form */}
                {showReviewForm && (
                  <form onSubmit={handleReviewSubmit} className="mt-6 p-6 bg-gray-50 rounded-xl">
                    <h3 className="font-semibold mb-4">Share your experience with {getFirstName(guide?.name)}</h3>
                    
                    {/* Star Rating Selector */}
                    <div className="mb-4">
                      <Label className="mb-2 block">Your Rating *</Label>
                      <div className="flex items-center gap-1" data-testid="star-rating-input">
                        {[1,2,3,4,5].map(star => (
                          <button
                            key={star}
                            type="button"
                            onClick={() => setReviewForm({ ...reviewForm, rating: star })}
                            className="p-0.5 transition-transform hover:scale-110"
                          >
                            <Star className={`w-8 h-8 cursor-pointer ${star <= reviewForm.rating ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300 hover:text-yellow-200'}`} />
                          </button>
                        ))}
                        <span className="ml-2 text-sm text-gray-500 font-medium">
                          {reviewForm.rating === 1 && 'Poor'}
                          {reviewForm.rating === 2 && 'Fair'}
                          {reviewForm.rating === 3 && 'Good'}
                          {reviewForm.rating === 4 && 'Great'}
                          {reviewForm.rating === 5 && 'Excellent'}
                        </span>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                      <div>
                        <Label htmlFor="reviewer_name">Your Name *</Label>
                        <Input
                          id="reviewer_name"
                          value={reviewForm.reviewer_name}
                          onChange={(e) => setReviewForm({ ...reviewForm, reviewer_name: e.target.value })}
                          placeholder="John Doe"
                          required
                        />
                      </div>
                      <div>
                        <Label htmlFor="tour_date">Tour Date</Label>
                        <Input
                          id="tour_date"
                          type="date"
                          value={reviewForm.tour_date}
                          onChange={(e) => setReviewForm({ ...reviewForm, tour_date: e.target.value })}
                        />
                      </div>
                    </div>

                    <div className="mb-4">
                      <Label htmlFor="review_comment">Your Review *</Label>
                      <Textarea
                        id="review_comment"
                        value={reviewForm.comment}
                        onChange={(e) => setReviewForm({ ...reviewForm, comment: e.target.value })}
                        placeholder="Tell others about your tour experience..."
                        rows={4}
                        required
                      />
                    </div>

                    <Button
                      type="submit"
                      disabled={submittingReview}
                      className="w-full bg-black hover:bg-gray-800"
                    >
                      {submittingReview ? 'Submitting...' : 'Submit Review'}
                      <Send className="w-4 h-4 ml-2" />
                    </Button>
                  </form>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Booking Sidebar - Right Side */}
          <div className="lg:col-span-1">
            <div className="sticky top-24">
              <Card className="shadow-lg">
                <CardContent className="p-6">
                  {/* Guide Mini Profile */}
                  <div className="flex items-center gap-3 mb-6 pb-6 border-b">
                    {guide.photo_urls && guide.photo_urls[0] ? (
                      <img 
                        src={guide.photo_urls[0]} 
                        alt={guide.name}
                        className={`w-12 h-12 rounded-full ${
                          guide.photo_position === 'full' ? 'object-contain' :
                          guide.photo_position === 'top' ? 'object-cover object-top' : 
                          guide.photo_position === 'bottom' ? 'object-cover object-bottom' : 
                          'object-cover object-center'
                        }`}
                      />
                    ) : (
                      <div className="w-12 h-12 rounded-full bg-gray-200 flex items-center justify-center">
                        <User className="w-6 h-6 text-gray-400" />
                      </div>
                    )}
                    <div>
                      <p className="font-medium">Hosted by</p>
                      <p className="text-sm text-gray-600">{guide.name}</p>
                    </div>
                  </div>

                  {/* School Name */}
                  <div className="mb-6">
                    <p className="text-xs text-gray-500 uppercase mb-1">School</p>
                    <p className="font-bold">{guide.school}</p>
                  </div>

                  {/* Pricing Display */}
                  {(guide.price_campus_tour || guide.price_video_chat) && (
                    <div className="mb-6">
                      <p className="text-xs text-gray-500 uppercase mb-2">Starting from</p>
                      <div className="flex items-baseline gap-1">
                        <span className="text-3xl font-bold">
                          ${Math.min(guide.price_campus_tour || 999, guide.price_video_chat || 999)}
                        </span>
                        <span className="text-gray-500">/ tour</span>
                      </div>
                    </div>
                  )}

                  {/* Tour Type Selection with Prices */}
                  <div className="mb-6">
                    <p className="text-xs text-gray-500 uppercase mb-2">Tour type</p>
                    <div className="space-y-2">
                      <label className="flex items-center justify-between cursor-pointer p-2 rounded-lg hover:bg-gray-50">
                        <div className="flex items-center gap-2">
                          <input type="radio" name="tourType" defaultChecked className="accent-black" />
                          <span>Campus tour</span>
                        </div>
                        {guide.price_campus_tour && (
                          <span className="text-sm font-semibold text-green-700">${guide.price_campus_tour}</span>
                        )}
                      </label>
                      <label className="flex items-center justify-between cursor-pointer p-2 rounded-lg hover:bg-gray-50">
                        <div className="flex items-center gap-2">
                          <input type="radio" name="tourType" className="accent-black" />
                          <span>Video Tour</span>
                        </div>
                        {guide.price_video_chat && (
                          <span className="text-sm font-semibold text-blue-700">${guide.price_video_chat}</span>
                        )}
                      </label>
                    </div>
                  </div>

                  {/* Availability */}
                  {(guide.availability_display || guide.availability) && (
                    <div className="mb-6">
                      <p className="text-xs text-gray-500 uppercase mb-2">Availability</p>
                      <div className="bg-gray-50 rounded-lg p-3">
                        {guide.availability_display ? (
                          <p className="text-sm text-gray-700 font-medium">{guide.availability_display}</p>
                        ) : (
                          <p className="text-sm text-gray-600">{guide.availability}</p>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Reserve Button - Shows Tour Details First */}
                  {(guide.price_campus_tour || guide.price_video_chat) && (
                    <div className="mb-4 bg-blue-50 rounded-lg p-3 text-center">
                      <p className="text-xs text-blue-700">Only <strong>10% deposit</strong> required to book. Pay the rest after your tour.</p>
                    </div>
                  )}
                  <Button 
                    onClick={() => setShowTourDetails(true)}
                    className="w-full bg-black hover:bg-gray-800 text-white py-6 text-lg"
                  >
                    Reserve a Tour
                  </Button>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </main>

      {/* Tour Details Modal - Pre-Booking */}
      {showTourDetails && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <Card className="w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <CardContent className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-bold">Tour Details</h2>
                <button onClick={() => setShowTourDetails(false)} className="text-gray-500 hover:text-black">
                  <X className="w-6 h-6" />
                </button>
              </div>

              {/* Guide Info Header */}
              <div className="flex items-center gap-4 mb-6 pb-4 border-b">
                {guide.photo_urls && guide.photo_urls[0] ? (
                  <img 
                    src={guide.photo_urls[0]} 
                    alt={guide.name}
                    className={`w-16 h-16 rounded-full ${
                      guide.photo_position === 'full' ? 'object-contain' :
                      guide.photo_position === 'top' ? 'object-cover object-top' : 
                      guide.photo_position === 'bottom' ? 'object-cover object-bottom' : 
                      'object-cover object-center'
                    }`}
                  />
                ) : (
                  <div className="w-16 h-16 rounded-full bg-gray-200 flex items-center justify-center">
                    <User className="w-8 h-8 text-gray-400" />
                  </div>
                )}
                <div>
                  <h3 className="font-bold text-lg">{guide.name}</h3>
                  <p className="text-gray-600 text-sm">{guide.school}</p>
                </div>
              </div>

              {/* Tour Options - Selectable */}
              <div className="space-y-4 mb-6">
                <h3 className="font-semibold text-gray-800">Select Tour Type</h3>
                
                {/* Campus Tour - Clickable */}
                <button
                  onClick={() => setBookingForm({...bookingForm, tourType: 'Campus tour'})}
                  className={`w-full text-left rounded-xl p-4 border-2 transition-all ${
                    bookingForm.tourType === 'Campus tour' 
                      ? 'bg-gray-100 border-black ring-2 ring-black ring-offset-2' 
                      : 'bg-gray-50 border-gray-200 hover:border-gray-400'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                      bookingForm.tourType === 'Campus tour' ? 'bg-black' : 'bg-gray-400'
                    }`}>
                      <MapPin className="w-5 h-5 text-white" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <h4 className="font-bold">Campus Tour</h4>
                          {guide.price_campus_tour && (
                            <span className="bg-green-100 text-green-700 text-sm font-bold px-2 py-0.5 rounded">
                              ${guide.price_campus_tour}
                            </span>
                          )}
                        </div>
                        {bookingForm.tourType === 'Campus tour' && (
                          <CheckCircle className="w-5 h-5 text-green-600" />
                        )}
                      </div>
                      <p className="text-sm text-gray-600 mb-2">In-person walking tour around campus</p>
                      <ul className="text-xs text-gray-500 space-y-1">
                        <li className="flex items-center gap-1">
                          <Clock className="w-3 h-3" /> Duration: 60-90 minutes
                        </li>
                        {guide.meeting_point && (
                          <li className="flex items-center gap-1">
                            <MapPin className="w-3 h-3" /> Meet at: {guide.meeting_point}
                          </li>
                        )}
                      </ul>
                    </div>
                  </div>
                </button>

                {/* Video Tour - Clickable */}
                <button
                  onClick={() => setBookingForm({...bookingForm, tourType: 'Video Tour'})}
                  className={`w-full text-left rounded-xl p-4 border-2 transition-all ${
                    bookingForm.tourType === 'Video Tour' 
                      ? 'bg-blue-100 border-blue-600 ring-2 ring-blue-600 ring-offset-2' 
                      : 'bg-blue-50 border-blue-200 hover:border-blue-400'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                      bookingForm.tourType === 'Video Tour' ? 'bg-blue-600' : 'bg-blue-400'
                    }`}>
                      <Video className="w-5 h-5 text-white" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <h4 className="font-bold">Video Tour</h4>
                          {guide.price_video_chat && (
                            <span className="bg-blue-100 text-blue-700 text-sm font-bold px-2 py-0.5 rounded">
                              ${guide.price_video_chat}
                            </span>
                          )}
                        </div>
                        {bookingForm.tourType === 'Video Tour' && (
                          <CheckCircle className="w-5 h-5 text-green-600" />
                        )}
                      </div>
                      <p className="text-sm text-gray-600 mb-2">Virtual meeting to learn about {guide.school}</p>
                      <ul className="text-xs text-gray-500 space-y-1">
                        <li className="flex items-center gap-1">
                          <Clock className="w-3 h-3" /> Duration: ~60 minutes
                        </li>
                        <li className="flex items-center gap-1">
                          <CheckCircle className="w-3 h-3 text-green-600" /> Flexible scheduling
                        </li>
                      </ul>
                    </div>
                  </div>
                </button>
              </div>

              {/* What's Included */}
              <div className="mb-6">
                <h3 className="font-semibold text-gray-800 mb-3">What's Included</h3>
                <ul className="space-y-2">
                  <li className="flex items-center gap-2 text-sm">
                    <CheckCircle className="w-4 h-4 text-green-600" />
                    <span>Personal tour with a current student</span>
                  </li>
                  <li className="flex items-center gap-2 text-sm">
                    <CheckCircle className="w-4 h-4 text-green-600" />
                    <span>Insider tips about campus life</span>
                  </li>
                  <li className="flex items-center gap-2 text-sm">
                    <CheckCircle className="w-4 h-4 text-green-600" />
                    <span>Q&A about academics, dorms, social life</span>
                  </li>
                  <li className="flex items-center gap-2 text-sm">
                    <CheckCircle className="w-4 h-4 text-green-600" />
                    <span>Personalized experience based on your interests</span>
                  </li>
                </ul>
              </div>

              {/* Availability */}
              {(guide.availability_display || guide.availability) && (
                <div className="mb-6">
                  <h3 className="font-semibold text-gray-800 mb-2">Availability</h3>
                  <div className="bg-gray-50 rounded-lg p-3">
                    <p className="text-sm text-gray-700">
                      {guide.availability_display || guide.availability}
                    </p>
                  </div>
                </div>
              )}

              {/* Important Info */}
              <div className="mb-6 bg-yellow-50 rounded-lg p-4 border border-yellow-200">
                <h3 className="font-semibold text-yellow-800 mb-2">Good to Know</h3>
                <ul className="text-sm text-yellow-700 space-y-1">
                  <li>• Free cancellation up to 24 hours before</li>
                  <li>• Guide will confirm your booking within 24 hours</li>
                  <li>• Questions? Message your guide after booking</li>
                </ul>
              </div>

              {/* Deposit Info */}
              <div className="bg-blue-50 rounded-lg p-3 text-center mb-4">
                <p className="text-sm text-blue-700">Only <strong>10% deposit</strong> to reserve. Pay the rest after your tour.</p>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3">
                <Button 
                  variant="outline"
                  onClick={() => setShowTourDetails(false)}
                  className="flex-1"
                >
                  Back
                </Button>
                <Button 
                  onClick={() => {
                    setShowTourDetails(false);
                    setShowBookingModal(true);
                  }}
                  className="flex-1 bg-black hover:bg-gray-800 text-white"
                >
                  Continue to Book
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Booking Modal */}
      {showBookingModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <Card className="w-full max-w-md max-h-[90vh] overflow-y-auto">
            <CardContent className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-bold">
                  {guide.email === 'saralyons1993@gmail.com' ? 'Contact Sara Lyons' : `Book a Tour with ${getFirstName(guide.name)}`}
                </h2>
                <button onClick={() => setShowBookingModal(false)} className="text-gray-500 hover:text-black">
                  <X className="w-6 h-6" />
                </button>
              </div>

              {/* Special message for Sara Lyons - Tour Guide Instructor */}
              {guide.email === 'saralyons1993@gmail.com' ? (
                <div className="text-center py-8">
                  <div className="mb-6">
                    <GraduationCap className="w-16 h-16 mx-auto text-blue-600 mb-4" />
                    <h3 className="text-xl font-semibold mb-2">Become a Tour Guide</h3>
                    <p className="text-gray-600 mb-4">
                      Feel free to reach out to Sara on her email to learn how to become a tour guide.
                    </p>
                  </div>
                  <a 
                    href="mailto:saralyons1993@gmail.com" 
                    className="inline-flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-medium transition-colors"
                  >
                    <Mail className="w-5 h-5" />
                    saralyons1993@gmail.com
                  </a>
                </div>
              ) : (
              <form onSubmit={handleBookingSubmit} className="space-y-4">
                <div>
                  <Label>Your Name *</Label>
                  <Input
                    required
                    value={bookingForm.name}
                    onChange={(e) => setBookingForm({...bookingForm, name: e.target.value})}
                    placeholder="Enter your name"
                  />
                </div>

                <div>
                  <Label>Email *</Label>
                  <Input
                    type="email"
                    required
                    value={bookingForm.email}
                    onChange={(e) => setBookingForm({...bookingForm, email: e.target.value})}
                    placeholder="your@email.com"
                  />
                </div>

                <div>
                  <Label>Phone *</Label>
                  <Input
                    type="tel"
                    required
                    value={bookingForm.phone}
                    onChange={(e) => setBookingForm({...bookingForm, phone: e.target.value})}
                    placeholder="(555) 123-4567"
                  />
                </div>

                <div>
                  <Label>Tour Type *</Label>
                  <select 
                    className="w-full border rounded-md p-2"
                    value={bookingForm.tourType}
                    onChange={(e) => setBookingForm({...bookingForm, tourType: e.target.value})}
                  >
                    <option value="Campus tour">Campus tour</option>
                    <option value="Video Tour">Video Tour</option>
                  </select>
                </div>

                {/* Time Selection Toggle */}
                <div className="border rounded-lg p-4 bg-gray-50">
                  <Label className="mb-3 block">How would you like to schedule?</Label>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => setBookingForm({...bookingForm, isCustomTimeRequest: false, customTimeNote: ''})}
                      className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-colors ${
                        !bookingForm.isCustomTimeRequest 
                          ? 'bg-black text-white' 
                          : 'bg-white border text-gray-700 hover:bg-gray-100'
                      }`}
                    >
                      Select Available Time
                    </button>
                    <button
                      type="button"
                      onClick={() => setBookingForm({...bookingForm, isCustomTimeRequest: true, date: '', time: ''})}
                      className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-colors ${
                        bookingForm.isCustomTimeRequest 
                          ? 'bg-black text-white' 
                          : 'bg-white border text-gray-700 hover:bg-gray-100'
                      }`}
                    >
                      Request a Time
                    </button>
                  </div>
                </div>

                {/* Standard Date/Time Selection */}
                {!bookingForm.isCustomTimeRequest && (
                  <>
                    <div>
                      <Label>Preferred Date *</Label>
                      <Input
                        type="date"
                        required
                        value={bookingForm.date}
                        onChange={(e) => setBookingForm({...bookingForm, date: e.target.value})}
                        min={new Date().toISOString().split('T')[0]}
                      />
                    </div>

                    <div>
                      <Label>Preferred Time *</Label>
                      <select 
                        className="w-full border rounded-md p-2"
                        required
                        value={bookingForm.time}
                        onChange={(e) => setBookingForm({...bookingForm, time: e.target.value})}
                      >
                        <option value="">Select time</option>
                        {['9:00 AM', '10:00 AM', '11:00 AM', '12:00 PM', '1:00 PM', '2:00 PM', '3:00 PM', '4:00 PM', '5:00 PM'].map(time => (
                          <option key={time} value={time}>{time}</option>
                        ))}
                      </select>
                    </div>
                  </>
                )}

                {/* Custom Time Request */}
                {bookingForm.isCustomTimeRequest && (
                  <div className="border-l-4 border-blue-500 pl-4 bg-blue-50 p-4 rounded-r-lg">
                    <Label className="text-blue-800">Request Your Preferred Time *</Label>
                    <p className="text-xs text-blue-600 mb-2">
                      Let the guide know when you'd like to meet. They'll confirm if the time works.
                    </p>
                    <Textarea
                      required
                      value={bookingForm.customTimeNote}
                      onChange={(e) => setBookingForm({...bookingForm, customTimeNote: e.target.value})}
                      placeholder="e.g., I'm available on weekends after 2pm, or any weekday morning next week. My timezone is EST."
                      rows={3}
                      className="bg-white"
                    />
                  </div>
                )}

                <div>
                  <Label>Message (optional)</Label>
                  <Textarea
                    value={bookingForm.message}
                    onChange={(e) => setBookingForm({...bookingForm, message: e.target.value})}
                    placeholder="Any specific questions or requests?"
                    rows={3}
                  />
                </div>

                <Button 
                  type="submit" 
                  disabled={isSubmitting || (bookingForm.isCustomTimeRequest ? !bookingForm.customTimeNote : (!bookingForm.date || !bookingForm.time))}
                  className="w-full bg-black hover:bg-gray-800 text-white py-6"
                >
                  {isSubmitting ? 'Submitting...' : (bookingForm.isCustomTimeRequest ? 'Send Time Request' : 'Send Booking Request')}
                </Button>
              </form>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default GuideProfile;
